'use client'

import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from '@/components/ui/menubar'
import { siteSections } from '@/lib/site-sections'
import { ArrowUpRight, Monitor, Moon, Palette, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import AboutDialog from './about-dialog'

const navMap = Object.fromEntries(
  siteSections
    .filter(({ href }) => href.startsWith('/'))
    .filter(
      ({ href }) =>
        !['/', '/about', '/contact', '/contribute', '/updates'].includes(href)
    )
    .map((s) => [s.href, s.label])
)

const viewNav = [
  '/models/leaderboard',
  '/voice-models/leaderboard',
  '/milestones',
  '|',
  '/models',
  '/voice-models',
  '/companies',
  '/people',
  '|',
  '/assistants',
  '/tools',
  '/mcp',
  '|',
  '/guides',
  '/glossary',
  '/links',
]

export const OsNav = () => {
  const [time, setTime] = useState<Date | null>(null)
  const [aboutOpen, setAboutOpen] = useState(false)

  const { theme, resolvedTheme, setTheme } = useTheme()

  const router = useRouter()

  useEffect(() => {
    setTime(new Date())
    const i = setInterval(() => setTime(new Date()), 1000)
    return () => clearInterval(i)
  }, [])

  return (
    <div className="fixed top-0 left-0 right-0 z-40 flex h-[51px] items-center justify-between border-b border-border/50 bg-background/50 px-4 backdrop-blur font-[Din]">
      <Menubar className="h-full border-none bg-transparent p-0 shadow-none">
        <MenubarMenu>
          <MenubarTrigger>Benched.ai</MenubarTrigger>
          <MenubarContent className="overflow-visible">
            <MenubarItem onClick={() => router.push('/')}>Home</MenubarItem>
            <MenubarSeparator />
            <MenubarItem onClick={() => router.push('/ai-history')}>
              History of AI
            </MenubarItem>
            <MenubarItem onClick={() => router.push('/jobs')}>
              Jobs Board
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem
              onClick={() =>
                window.dispatchEvent(new Event('open-command-palette'))
              }
            >
              Search
            </MenubarItem>
            <MenubarItem onClick={() => router.push('/rss.xml')}>
              RSS Feed <ArrowUpRight className="ml-auto" />
            </MenubarItem>
            <MenubarItem onClick={() => router.push('/llms.txt')}>
              LLMs.txt <ArrowUpRight className="ml-auto" />
            </MenubarItem>
            <MenubarSeparator />
            <MenubarSub>
              <MenubarSubTrigger>Display</MenubarSubTrigger>
              <MenubarSubContent>
                <MenubarItem
                  onClick={() =>
                    setTheme(
                      theme === 'system'
                        ? resolvedTheme === 'dark'
                          ? 'light'
                          : 'dark'
                        : theme === 'dark'
                          ? 'light'
                          : 'dark'
                    )
                  }
                >
                  <Palette className="mr-2 h-4 w-4" />
                  <span>Toggle color</span>
                </MenubarItem>
                <MenubarItem onClick={() => setTheme('system')}>
                  <Monitor className="mr-2 h-4 w-4" />
                  <span>Use system preferences</span>
                </MenubarItem>
                <MenubarItem onClick={() => setTheme('dark')}>
                  <Moon className="mr-2 h-4 w-4" />
                  <span>Dark mode</span>
                </MenubarItem>
                <MenubarItem onClick={() => setTheme('light')}>
                  <Sun className="mr-2 h-4 w-4" />
                  <span>Light mode</span>
                </MenubarItem>
              </MenubarSubContent>
            </MenubarSub>
          </MenubarContent>
        </MenubarMenu>

        <MenubarMenu>
          <MenubarTrigger>View</MenubarTrigger>
          <MenubarContent>
            {viewNav.map((href, i) =>
              href === '|' ? (
                <MenubarSeparator key={`sep-${i}`} />
              ) : (
                <MenubarItem key={href} onClick={() => router.push(href)}>
                  {navMap[href]}
                </MenubarItem>
              )
            )}
          </MenubarContent>
        </MenubarMenu>

        <MenubarMenu>
          <MenubarTrigger>About</MenubarTrigger>
          <MenubarContent>
            
            <MenubarItem onClick={() => router.push('/contribute')}>
              Contribute
            </MenubarItem>
            <MenubarItem onClick={() => router.push('/methodology')}>
              Methodology
            </MenubarItem>
            <MenubarItem onClick={() => router.push('/advertising')}>
              Advertising
            </MenubarItem>
            <MenubarItem onClick={() => router.push('/consulting')}>
              Consulting
            </MenubarItem>
            <MenubarItem onClick={() => router.push('/updates')}>
              Updates
            </MenubarItem>
            <MenubarSeparator />
            <MenubarItem onClick={() => setAboutOpen(true)}>About</MenubarItem>
          </MenubarContent>
        </MenubarMenu>
      </Menubar>
      <div className="font-mono text-xs">
        {time ? (
          <>
            <span>
              {time.toLocaleDateString('en-US', {
                timeZone: 'America/Los_Angeles',
              })}
            </span>
            <span className="mx-1 text-foreground-lighter/50">|</span>
            <span>
              {time.toLocaleTimeString('en-US', {
                timeZone: 'America/Los_Angeles',
              })}
            </span>
          </>
        ) : (
          ''
        )}
      </div>
      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
    </div>
  )
}

export default OsNav
